import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { tokens } from '../tokens';

const ARROW_SIZE = 6;
const TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 8;

function getTooltipPosition(triggerRect, tooltipRect, preferred) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const positions = {
    top: {
      top: triggerRect.top - tooltipRect.height - TOOLTIP_GAP - ARROW_SIZE,
      left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
      arrow: 'bottom',
    },
    bottom: {
      top: triggerRect.bottom + TOOLTIP_GAP + ARROW_SIZE,
      left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
      arrow: 'top',
    },
    left: {
      top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
      left: triggerRect.left - tooltipRect.width - TOOLTIP_GAP - ARROW_SIZE,
      arrow: 'right',
    },
    right: {
      top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
      left: triggerRect.right + TOOLTIP_GAP + ARROW_SIZE,
      arrow: 'left',
    },
  };

  // Try preferred position first, then fallbacks
  const order = [preferred, 'top', 'bottom', 'right', 'left'].filter(
    (v, i, a) => a.indexOf(v) === i
  );

  for (const pos of order) {
    const p = positions[pos];
    if (
      p.top >= VIEWPORT_PADDING &&
      p.left >= VIEWPORT_PADDING &&
      p.top + tooltipRect.height <= vh - VIEWPORT_PADDING &&
      p.left + tooltipRect.width <= vw - VIEWPORT_PADDING
    ) {
      return p;
    }
  }

  // Fallback: clamp to viewport
  const p = positions[preferred];
  return {
    ...p,
    top: Math.max(VIEWPORT_PADDING, Math.min(p.top, vh - tooltipRect.height - VIEWPORT_PADDING)),
    left: Math.max(VIEWPORT_PADDING, Math.min(p.left, vw - tooltipRect.width - VIEWPORT_PADDING)),
  };
}

const arrowStyles = {
  top: {
    bottom: -ARROW_SIZE,
    left: '50%',
    transform: 'translateX(-50%)',
    borderLeft: `${ARROW_SIZE}px solid transparent`,
    borderRight: `${ARROW_SIZE}px solid transparent`,
    borderTop: `${ARROW_SIZE}px solid ${tokens.colors.neutral[800]}`,
  },
  bottom: {
    top: -ARROW_SIZE,
    left: '50%',
    transform: 'translateX(-50%)',
    borderLeft: `${ARROW_SIZE}px solid transparent`,
    borderRight: `${ARROW_SIZE}px solid transparent`,
    borderBottom: `${ARROW_SIZE}px solid ${tokens.colors.neutral[800]}`,
  },
  left: {
    right: -ARROW_SIZE,
    top: '50%',
    transform: 'translateY(-50%)',
    borderTop: `${ARROW_SIZE}px solid transparent`,
    borderBottom: `${ARROW_SIZE}px solid transparent`,
    borderLeft: `${ARROW_SIZE}px solid ${tokens.colors.neutral[800]}`,
  },
  right: {
    left: -ARROW_SIZE,
    top: '50%',
    transform: 'translateY(-50%)',
    borderTop: `${ARROW_SIZE}px solid transparent`,
    borderBottom: `${ARROW_SIZE}px solid transparent`,
    borderRight: `${ARROW_SIZE}px solid ${tokens.colors.neutral[800]}`,
  },
};

const Tooltip = ({
  content,
  position = 'top',
  delay = 200,
  maxWidth = 260,
  children,
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timerRef = useRef(null);

  const show = useCallback(() => {
    timerRef.current = setTimeout(() => setVisible(true), delay);
  }, [delay]);

  const hide = useCallback(() => {
    clearTimeout(timerRef.current);
    setVisible(false);
    setCoords(null);
  }, []);

  // Position the tooltip when it becomes visible
  useEffect(() => {
    if (!visible || !triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const pos = getTooltipPosition(triggerRect, tooltipRect, position);
    setCoords(pos);
  }, [visible, position]);

  // Clean up timer
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  if (!content) return children;

  const tooltipElement = visible
    ? ReactDOM.createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{
            position: 'fixed',
            top: coords ? coords.top : -9999,
            left: coords ? coords.left : -9999,
            zIndex: tokens.zIndex.tooltip,
            backgroundColor: tokens.colors.neutral[800],
            color: tokens.colors.dark.text,
            fontSize: tokens.typography.fontSize.sm[0],
            lineHeight: '1.5',
            padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
            borderRadius: tokens.borderRadius.md,
            maxWidth,
            boxShadow: tokens.boxShadow.lg,
            pointerEvents: 'none',
            opacity: coords ? 1 : 0,
            transition: 'opacity 150ms ease',
          }}
        >
          {content}
          {coords && (
            <div
              style={{
                position: 'absolute',
                width: 0,
                height: 0,
                ...arrowStyles[coords.arrow],
              }}
            />
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        style={{ display: 'inline-flex' }}
      >
        {children}
      </span>
      {tooltipElement}
    </>
  );
};

export default Tooltip;
